import type { UUID } from './common';
import type { OrderStatus } from './order';

export const MAX_COURIER_BATCH_ORDERS = 50;

export const COURIER_ASSIGNABLE_ORDER_STATUSES = ['ordered', 'shipped'] as const;
export type CourierAssignableOrderStatus = (typeof COURIER_ASSIGNABLE_ORDER_STATUSES)[number];

export interface Courier {
  readonly id: UUID;
  readonly store_id: UUID;
  readonly name: string;
  readonly phone_number: string;
  readonly active: boolean;
  readonly verification_status: CourierVerificationStatus;
  readonly verification_source: CourierVerificationSource;
  readonly verified_at: string | null;
  readonly verified_by_user_id: UUID | null;
  readonly internal_notes: string | null;
  readonly portal_link?: CourierPortalLinkSummary;
  readonly created_at: string;
  readonly updated_at: string;
  readonly deleted_at: string | null;
}

export interface CourierInput {
  readonly storeId: UUID;
  readonly name: string;
  readonly phoneNumber: string;
  readonly active: boolean;
  readonly internalNotes: string | null;
}

export interface CourierUpdateInput extends CourierInput {
  readonly courierId: UUID;
}

export interface CourierSummary {
  readonly id: UUID;
  readonly name: string;
  readonly phone_number: string;
  readonly active: boolean;
  readonly verification_status: CourierVerificationStatus;
}

export type CourierVerificationStatus = 'pending' | 'approved';
export type CourierVerificationSource = 'merchant_otp' | 'legacy_backfill';

export type CourierOtpProviderStatus =
  | 'reserved'
  | 'sent'
  | 'failed'
  | 'timeout_after_acceptance_unknown';

export type CourierVerificationAction = 'create' | 'resend' | 'verify';

export type CourierVerificationErrorCode =
  | 'UNAUTHORIZED'
  | 'COURIER_NOT_FOUND'
  | 'COURIER_INACTIVE'
  | 'COURIER_DELETED'
  | 'ALREADY_VERIFIED'
  | 'OTP_INVALID'
  | 'OTP_EXPIRED'
  | 'OTP_COOLDOWN'
  | 'OTP_TOO_MANY_ATTEMPTS'
  | 'OTP_NOT_FOUND'
  | 'SMS_PROVIDER_UNAVAILABLE'
  | 'UNKNOWN';

export interface CourierVerificationResult {
  readonly success: boolean;
  readonly error_code: CourierVerificationErrorCode | null;
  readonly courier: Courier | null;
  readonly expires_at: string | null;
  readonly resend_available_at: string | null;
  readonly attempts_remaining: number | null;
  readonly debug_otp?: string | null;
}

export interface CreateCourierVerificationInput {
  readonly storeId: UUID;
  readonly courierId: UUID;
}

export interface VerifyCourierOtpInput extends CreateCourierVerificationInput {
  readonly otp: string;
}

export interface CourierBatchSummary {
  readonly id: UUID;
  readonly store_id: UUID;
  readonly courier: CourierSummary;
  readonly display_code: string;
  readonly order_ids: readonly UUID[];
  readonly package_tags?: readonly string[];
  readonly order_count: number;
  readonly active_link_expires_at: string | null;
  readonly active_link_revoked_at: string | null;
  readonly created_at: string;
}

export type CourierAssignmentErrorCode =
  | 'DELIVERY_FULFILLMENT_ONLY'
  | 'UNAUTHORIZED'
  | 'STORE_NOT_FOUND'
  | 'COURIER_NOT_FOUND'
  | 'COURIER_INACTIVE'
  | 'COURIER_NOT_VERIFIED'
  | 'EMPTY_ORDER_SET'
  | 'TOO_MANY_ORDERS'
  | 'ORDER_UNAVAILABLE'
  | 'INVALID_STATUS'
  | 'MANUAL_APPROVAL_REQUIRED'
  | 'ASSIGNMENT_CONFLICT'
  | 'ASSIGNMENT_NOT_FOUND'
  | 'ASSIGNMENT_HAS_PROOF'
  | 'LINK_NOT_FOUND'
  | 'UNKNOWN';

export interface CourierAssignmentResult {
  readonly success: boolean;
  readonly error_code: CourierAssignmentErrorCode | null;
  readonly batch: CourierBatchSummary | null;
  readonly manifest_path: string | null;
  readonly manifest_url: string | null;
  readonly fragment_secret: string | null;
  readonly expires_at: string | null;
  readonly reused_existing: boolean;
  readonly revoked_batch_ids: readonly UUID[];
}

export interface CourierUnassignmentResult {
  readonly success: boolean;
  readonly error_code: CourierAssignmentErrorCode | null;
  readonly unassigned_order_ids: readonly UUID[];
  readonly revoked_batch_ids: readonly UUID[];
}

export interface CourierLinkActionResult {
  readonly success: boolean;
  readonly error_code: CourierAssignmentErrorCode | null;
  readonly batch_id: UUID | null;
  readonly manifest_path: string | null;
  readonly manifest_url: string | null;
  readonly fragment_secret: string | null;
  readonly expires_at: string | null;
  readonly reused_existing: boolean;
}

export type CourierPortalLinkStatus = 'available' | 'missing' | 'revoked' | 'unavailable';

export interface CourierPortalLinkSummary {
  readonly id: UUID | null;
  readonly status: CourierPortalLinkStatus;
  readonly created_at: string | null;
  readonly revoked_at: string | null;
  readonly last_accessed_at: string | null;
}

export type CourierPortalLinkAction = 'get_or_create' | 'revoke' | 'regenerate';

export type CourierPortalLinkErrorCode =
  | 'UNAUTHORIZED'
  | 'STORE_NOT_FOUND'
  | 'COURIER_NOT_FOUND'
  | 'COURIER_INACTIVE'
  | 'COURIER_NOT_VERIFIED'
  | 'LINK_NOT_FOUND'
  | 'RATE_LIMITED'
  | 'UNKNOWN';

export interface CourierPortalLinkActionInput {
  readonly storeId: UUID;
  readonly courierId: UUID;
  readonly action: CourierPortalLinkAction;
}

export interface CourierPortalLinkActionResult {
  readonly success: boolean;
  readonly error_code: CourierPortalLinkErrorCode | null;
  readonly courier_id: UUID | null;
  readonly portal_link_id: UUID | null;
  readonly portal_path: string | null;
  readonly portal_url: string | null;
  readonly fragment_secret: string | null;
  readonly created_at: string | null;
  readonly revoked_at: string | null;
  readonly regenerated: boolean;
}

export type PublicCourierManifestStatus = 'valid' | 'invalid';

export interface PublicCourierManifestOrder {
  readonly subtotal_cents: number;
  readonly delivery_fee_cents: number | null;
  readonly total_cents: number | null;
  readonly currency: string;
  readonly delivery_pricing_state: 'legacy_unconfigured' | 'priced';
  readonly batch_order_id: UUID;
  readonly display_id: string;
  readonly package_tag: string;
  readonly status: OrderStatus;
  readonly customer_name: string;
  readonly delivery_phone: string | null;
  readonly address: {
    readonly recipient: string;
    readonly line_1: string;
    readonly line_2: string | null;
    readonly city: string;
    readonly region: string;
    readonly postal_code: string;
    readonly country_code: string;
  };
  readonly location: {
    readonly latitude: number;
    readonly longitude: number;
  } | null;
  readonly customer_notes: string | null;
  readonly package_count: number;
  readonly proof_status: CourierProofStatus;
  readonly current_proof_submitted_at: string | null;
  readonly proof_replacement_allowed: boolean;
}

export interface PublicCourierManifest {
  readonly display_code: string;
  readonly expires_at: string;
  readonly store: {
    readonly name: string;
    readonly phone_number: string | null;
    readonly whatsapp_number: string | null;
  };
  readonly courier: {
    readonly name: string;
  };
  readonly orders: readonly PublicCourierManifestOrder[];
}

export interface PublicCourierManifestResult {
  readonly status: PublicCourierManifestStatus;
  readonly manifest: PublicCourierManifest | null;
}

export type PublicCourierPortalStatus = 'valid' | 'invalid' | 'rate_limited';

export interface PublicCourierPortalLocalityNames {
  readonly ar: string | null;
  readonly he: string;
  readonly en: string | null;
}

export interface PublicCourierPortalOrder {
  readonly subtotal_cents: number;
  readonly delivery_fee_cents: number | null;
  readonly total_cents: number | null;
  readonly currency: string;
  readonly delivery_pricing_state: 'legacy_unconfigured' | 'priced';
  readonly proof_ref: string;
  readonly display_id: string;
  readonly package_tag: string;
  readonly status: 'shipped';
  readonly customer_name: string;
  readonly delivery_phone: string | null;
  readonly delivery_method: 'address' | 'location';
  readonly address: {
    readonly recipient: string;
    readonly line_1: string;
    readonly line_2: string | null;
    readonly locality_names: PublicCourierPortalLocalityNames;
    readonly region: string | null;
    readonly postal_code: string | null;
    readonly country_code: 'IL';
  } | null;
  readonly location: {
    readonly latitude: number;
    readonly longitude: number;
    readonly locality_names: PublicCourierPortalLocalityNames;
  } | null;
  readonly customer_notes: string | null;
  readonly package_count: number;
  readonly proof_status: CourierProofStatus;
  readonly current_proof_submitted_at: string | null;
  readonly proof_replacement_allowed: boolean;
}

export interface PublicCourierPortalLocalityGroup {
  readonly locality_key: string;
  readonly symbol_number: number;
  readonly names: PublicCourierPortalLocalityNames;
  readonly package_count: number;
  readonly orders: readonly PublicCourierPortalOrder[];
}

export interface PublicCourierPortal {
  readonly store: {
    readonly name: string;
    readonly phone_number: string | null;
    readonly whatsapp_number: string | null;
  };
  readonly courier: {
    readonly name: string;
  };
  readonly localities: readonly PublicCourierPortalLocalityGroup[];
}

export interface PublicCourierPortalResult {
  readonly status: PublicCourierPortalStatus;
  readonly portal: PublicCourierPortal | null;
}

export interface CourierOrderAssignmentSummary {
  readonly batch_id: UUID;
  readonly batch_order_id?: UUID;
  readonly display_code: string;
  readonly package_tag?: string;
  readonly batch_order_count?: number;
  readonly courier_id: UUID;
  readonly courier_name: string;
  readonly courier_phone_number: string;
  readonly assigned_at: string;
  readonly link_expires_at: string | null;
  readonly link_revoked_at: string | null;
  readonly proof_status?: CourierProofStatus;
  readonly latest_proof_id?: UUID | null;
  readonly latest_proof_submitted_at?: string | null;
  readonly proof_admin_confirmed_at?: string | null;
}

export type CourierProofStatus =
  | 'none'
  | 'submitted'
  | 'awaiting_internal_confirmation'
  | 'internally_confirmed';

export interface CourierProofSummary {
  readonly id: UUID;
  readonly batch_order_id: UUID;
  readonly order_id: UUID;
  readonly package_tag: string;
  readonly submitted_at: string;
  readonly replaced_at: string | null;
  readonly admin_confirmed_at: string | null;
  readonly admin_confirmed_by_user_id: UUID | null;
}

export interface AdminCourierProof extends CourierProofSummary {
  readonly store_id: UUID;
  readonly batch_id: UUID;
  readonly link_id: UUID;
  readonly mime_type: 'image/jpeg' | 'image/png' | 'image/webp';
  readonly file_size_bytes: number;
  readonly signed_photo_url: string;
  readonly signed_photo_url_expires_at: string;
}

export type CourierProofErrorCode =
  | 'DELIVERY_FULFILLMENT_ONLY'
  | 'INVALID_LINK'
  | 'ORDER_NOT_IN_BATCH'
  | 'ASSIGNMENT_REPLACED'
  | 'INVALID_STATUS'
  | 'ALREADY_CONFIRMED'
  | 'IMAGE_REQUIRED'
  | 'IMAGE_TOO_LARGE'
  | 'IMAGE_INVALID_TYPE'
  | 'UPLOAD_FAILED'
  | 'SIGNED_URL_FAILED'
  | 'PROOF_MUTATION_FAILED'
  | 'UNKNOWN';

export interface CourierProofSubmissionResult {
  readonly success: boolean;
  readonly error_code: CourierProofErrorCode | null;
  readonly order_id: UUID | null;
  readonly batch_order_id: UUID | null;
  readonly proof: CourierProofSummary | null;
  readonly order_status: OrderStatus | null;
  readonly delivered_at: string | null;
  readonly idempotent_replay: boolean;
}

export interface SubmitCourierDeliveryProofInput {
  readonly batchId: UUID;
  readonly rawSecret: string;
  readonly batchOrderId: UUID;
  readonly submissionId: UUID;
  readonly photo: File;
}

export interface SubmitCourierPortalDeliveryProofInput {
  readonly portalLinkId: UUID;
  readonly rawSecret: string;
  readonly proofRef: string;
  readonly submissionId: UUID;
  readonly photo: File;
}

export type CourierErrorKey =
  | 'admin.orders.fulfillment.deliveryOnly'
  | 'admin.couriers.errors.nameRequired'
  | 'admin.couriers.errors.phoneInvalid'
  | 'admin.couriers.errors.notesTooLong'
  | 'admin.couriers.errors.phoneDuplicate'
  | 'admin.couriers.errors.permissionDenied'
  | 'admin.couriers.errors.networkFailed'
  | 'admin.couriers.errors.otpInvalid'
  | 'admin.couriers.errors.otpExpired'
  | 'admin.couriers.errors.otpCooldown'
  | 'admin.couriers.errors.tooManyAttempts'
  | 'admin.couriers.errors.alreadyVerified'
  | 'admin.couriers.errors.smsUnavailable'
  | 'admin.couriers.portal.errors.linkNotFound'
  | 'admin.couriers.portal.errors.unverifiedCourier'
  | 'admin.couriers.portal.errors.inactiveCourier'
  | 'admin.orders.courierAssignment.errors.invalidStatus'
  | 'admin.orders.courierAssignment.errors.manualApprovalRequired'
  | 'admin.orders.courierAssignment.errors.inactiveCourier'
  | 'admin.orders.courierAssignment.errors.unverifiedCourier'
  | 'admin.orders.courierAssignment.errors.ordersUnavailable'
  | 'admin.orders.courierAssignment.errors.tooManyOrders'
  | 'admin.orders.courierAssignment.errors.linkExpired'
  | 'admin.orders.courierAssignment.errors.assignmentConflict'
  | 'admin.orders.courierAssignment.errors.assignmentNotFound'
  | 'admin.orders.courierAssignment.errors.assignmentHasProof'
  | 'admin.orders.courierProof.errors.invalidLink'
  | 'admin.orders.courierProof.errors.notFound'
  | 'admin.orders.courierProof.errors.alreadyConfirmed'
  | 'admin.orders.courierProof.errors.signedUrlFailed'
  | 'storefront.courierManifest.invalid.title'
  | 'storefront.courierPortal.invalid.title'
  | 'storefront.courierManifest.proof.errors.invalidType'
  | 'storefront.courierManifest.proof.errors.tooLarge'
  | 'storefront.courierManifest.proof.errors.notShipped'
  | 'storefront.courierManifest.proof.errors.alreadyConfirmed'
  | 'storefront.courierManifest.proof.errors.uploadFailed';
